
import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, ArrowDownToLine } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import ZohoCRMScript from "../ZohoCRMScript";

const XDRKeyFeaturesSection: React.FC = () => {
  return (
    <>
      <ZohoCRMScript />
      <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-5" id="key-features">Key Features to Look For</h2>
      <p className="text-lg text-gray-700 mb-6">
        When evaluating AI-based XDR solutions, security leaders should prioritize the following key capabilities:
      </p>
      
      <div className="mb-10 space-y-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 md:w-1/2">
            <h4 className="font-bold text-lg mb-3">Unified Visibility</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Single console for all security data</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Cross-domain correlation capabilities</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Historical data retention for investigation</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Custom dashboard capabilities</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 md:w-1/2">
            <h4 className="font-bold text-lg mb-3">Advanced AI Capabilities</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Multiple ML models for different threat types</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Behavioral analytics for users and entities</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Explainable AI for alert context</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Continuous learning capabilities</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 md:w-1/2">
            <h4 className="font-bold text-lg mb-3">Automated Response</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Customizable response playbooks</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Multi-stage automated investigations</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Containment and remediation actions</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Human-in-the-loop options for critical decisions</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 md:w-1/2">
            <h4 className="font-bold text-lg mb-3">Integration Ecosystem</h4>
            <ul className="space-y-2">
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>API-driven architecture</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Pre-built integrations with security tools</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Cloud service provider integrations</span>
              </li>
              <li className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                <span>Threat intelligence platform connections</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="my-10 p-6 bg-[#f9f4fc] rounded-xl border border-[#8932c8]/20">
        <h3 className="text-xl font-bold mb-4 flex items-center">
          <ArrowDownToLine className="h-6 w-6 mr-2 text-[#8932c8]" />
          XDR Feature Evaluation Checklist
        </h3>
        <p className="mb-4">
          Download our comprehensive checklist for evaluating AI-XDR solutions, including:
        </p>
        <ul className="mb-6 space-y-2">
          <li className="flex items-center">
            <Check className="h-5 w-5 text-[#8932c8] mr-2" />
            <span>100+ technical and business criteria</span>
          </li>
          <li className="flex items-center">
            <Check className="h-5 w-5 text-[#8932c8] mr-2" />
            <span>Vendor comparison worksheet</span>
          </li>
          <li className="flex items-center">
            <Check className="h-5 w-5 text-[#8932c8] mr-2" />
            <span>ROI calculator template</span>
          </li>
          <li className="flex items-center">
            <Check className="h-5 w-5 text-[#8932c8] mr-2" />
            <span>Implementation planning guide</span>
          </li>
        </ul>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-[#8932c8] hover:bg-[#7a2cb3]">
              Get the Checklist
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Get the Checklist</DialogTitle>
            </DialogHeader>
            <div id='crmWebToEntityForm' className='zcwf_lblLeft crmWebToEntityForm' style={{backgroundColor: 'white', color: 'black', maxWidth: '600px'}}>
              <form id='webform966770000000572001' action='https://crm.zoho.in/crm/WebToLeadForm' name='WebToLeads966770000000572001' method='POST' onSubmit={() => { return (window as any).checkMandatory966770000000572001(); }} acceptCharset='UTF-8'>
                <input type='text' style={{display:'none'}} name='xnQsjsdp' value='2eaa648798ce23a7a9a9fdfb797621a05074c8311ce9031824d1b4d79807096f' />
                <input type='hidden' name='zc_gad' id='zc_gad' value='' />
                <input type='text' style={{display:'none'}} name='xmIwtLD' value='3b367f412ccfde6c347aca9059e7efedf4cd6eef72f9a51d2beb75a4850be285503386a2c820cee984c68e0e4df8b7e1' />
                <input type='text' style={{display:'none'}} name='actionType' value='TGVhZHM=' />
                <input type='text' style={{display:'none'}} name='returnURL' value='null' />
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                    <label htmlFor='First_Name'>First Name <span style={{color:'red'}}>*</span></label>
                  </div>
                  <div className='zcwf_col_fld'>
                    <input type='text' id='First_Name' aria-required='true' aria-label='First Name' name='First Name' maxLength={40} />
                  </div>
                </div>
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                    <label htmlFor='Last_Name'>Last Name <span style={{color:'red'}}>*</span></label>
                  </div>
                  <div className='zcwf_col_fld'>
                    <input type='text' id='Last_Name' aria-required='true' aria-label='Last Name' name='Last Name' maxLength={80} />
                  </div>
                </div>
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                    <label htmlFor='Email'>Work Email <span style={{color:'red'}}>*</span></label>
                  </div>
                  <div className='zcwf_col_fld'>
                    <input type='text' data-ftype='email' autoComplete='false' id='Email' aria-required='true' aria-label='Email' name='Email' maxLength={100} />
                  </div>
                </div>
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                    <label htmlFor='Company'>Company <span style={{color:'red'}}>*</span></label>
                  </div>
                  <div className='zcwf_col_fld'>
                    <input type='text' id='Company' aria-required='true' aria-label='Company' name='Company' maxLength={200} />
                  </div>
                </div>
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab' style={{fontSize:'12px', fontFamily: 'Arial'}}>
                    <label htmlFor='Description'>How can we help? <span style={{color:'red'}}>*</span></label>
                  </div>
                  <div className='zcwf_col_fld'>
                    <textarea style={{fontFamily: 'Arial, sans-serif'}} id='Description' aria-required='true' aria-label='Description' name='Description'></textarea>
                  </div>
                </div>
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab'></div>
                  <div className='zcwf_col_fld'>
                    <div style={{display: 'flex', alignItems: 'flex-start', marginBottom: '10px'}}>
                      <input type='checkbox' id='privacyConsent1' required style={{marginRight: '8px', marginTop: '2px'}} />
                      <label htmlFor='privacyConsent1' style={{fontSize: '12px', color: '#666', lineHeight: '1.4'}}>
                        I agree to the{" "}
                        <a href='/privacy-policy' target='_blank' style={{color: '#8932c8', textDecoration: 'underline'}}>
                          Privacy Policy
                        </a>{" "}
                        and consent to the processing of my personal data. <span style={{color: 'red'}}>*</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <input type='text' style={{display: 'none'}} name='aG9uZXlwb3Q' value='' />
                
                <div className='zcwf_row'>
                  <div className='zcwf_col_lab'></div>
                  <div className='zcwf_col_fld'>
                    <input type='submit' id='formsubmit' className='formsubmit zcwf_button' value='Submit' style={{backgroundColor: '#8932c8', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer'}} />
                    <input type='reset' className='zcwf_button' name='reset' value='Reset' style={{marginLeft: '10px', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer'}} />
                  </div>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
    </>
  );
};

export default XDRKeyFeaturesSection;
